// src/pages/MasterFiles.jsx
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import api from "../utils/api";
import { useAuth } from "../context/AuthContext";
import DogEditModal from "../components/DogEditModal";
import { buildPatchPayload } from "../utils/dogEditForm";

const Page = styled.div`
  min-height: 100vh;
  padding: 20px;
  background: linear-gradient(135deg, #fff7ed, #fff9f2);
`;

const Title = styled.h2`
  margin-bottom: 12px;
  color: #1f2937;
  font-size: 24px;
  font-weight: 700;
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;
`;

const ExportButton = styled.button`
  background: #059669;
  color: #fff;
  padding: 8px 16px;
  border-radius: 8px;
  border: none;
  font-weight: 600;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: background 0.2s;
  &:hover {
    background: #047857;
  }
  &:active {
    background: #065f46;
  }
`;

const BackButton = styled.button`
  background: #6b7280;
  color: #fff;
  padding: 8px 16px;
  border-radius: 8px;
  border: none;
  font-weight: 600;
  cursor: pointer;
  margin-bottom: 16px;
  &:hover {
    background: #4b5563;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: #fffdf9;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
`;

const Th = styled.th`
  background: #ffedd5;
  padding: 12px;
  text-align: left;
  font-weight: 600;
  color: #1f2937;
`;

const Td = styled.td`
  padding: 12px;
  border-bottom: 1px solid #f5f5f5;
  vertical-align: middle;
`;

const RouteRow = styled.tr`
  cursor: pointer;
  transition: background-color 0.2s;
  &:hover {
    background-color: #fff7ed;
  }
`;

const DogsContainer = styled.div`
  margin-top: 24px;
`;

const DogsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 16px;
`;

const DogCard = styled.div`
  background: #ffffff;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid #f3f4f6;
  transition:
    transform 0.2s,
    box-shadow 0.2s;
  display: flex;
  gap: 20px;
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  }
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const ImageCarouselContainer = styled.div`
  position: relative;
  min-width: 300px;
  width: 300px;
  flex-shrink: 0;
  @media (max-width: 768px) {
    width: 100%;
    min-width: 100%;
  }
`;

const CarouselWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 300px;
  border-radius: 12px;
  overflow: hidden;
  background: #f9fafb;
  border: 2px solid #ffedd5;
`;

const CarouselImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: ${(props) => (props.active ? "block" : "none")};
`;

const CarouselButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.6);
  color: white;
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: bold;
  z-index: 10;
  transition: background 0.2s;
  &:hover {
    background: rgba(0, 0, 0, 0.8);
  }
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
  ${(props) => (props.direction === "prev" ? "left: 10px;" : "right: 10px;")}
`;

const CarouselDots = styled.div`
  display: flex;
  justify-content: center;
  gap: 6px;
  margin-top: 12px;
`;

const Dot = styled.button`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  border: none;
  background: ${(props) => (props.active ? "#c2410c" : "#d1d5db")};
  cursor: pointer;
  padding: 0;
  transition: background 0.2s;
  &:hover {
    background: ${(props) => (props.active ? "#c2410c" : "#9ca3af")};
  }
`;

const CardContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const DogCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 12px;
  border-bottom: 2px solid #ffedd5;
`;

const DogUID = styled.h3`
  margin: 0;
  color: #c2410c;
  font-size: 18px;
  font-weight: 700;
`;

const StatusBadge = styled.span`
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background: ${(props) => {
    if (props.status === "ACTIVE") return "#d1fae5";
    return "#fee2e2";
  }};
  color: ${(props) => {
    if (props.status === "ACTIVE") return "#065f46";
    return "#991b1b";
  }};
`;

const DetailsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 16px;
`;

const Section = styled.div`
  margin-bottom: 0;
`;

const SectionTitle = styled.h4`
  margin: 0 0 8px 0;
  color: #374151;
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  font-size: 14px;
  color: #6b7280;
`;

const InfoLabel = styled.span`
  font-weight: 500;
  color: #4b5563;
`;

const InfoValue = styled.span`
  color: #1f2937;
  font-weight: 600;
`;

const VaccinationBlock = styled.div`
  margin-top: 8px;
  padding-top: 16px;
  border-top: 2px solid #ffedd5;
`;

const ObservationsBlock = styled(VaccinationBlock)``;

const ObservationsSummary = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 24px;
  margin-bottom: 10px;
`;

const ObservationsSummaryItem = styled.div`
  display: flex;
  gap: 6px;
  align-items: baseline;
  font-size: 13px;
`;

const VaccinationTable = styled.div`
  display: flex;
  flex-direction: column;
  background: #fffbf7;
  border-radius: 8px;
  border: 1px solid #ffedd5;
  overflow: hidden;
`;

const VaccinationHeaderRow = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 1fr 1fr 1fr;
  gap: 12px;
  padding: 10px 14px;
  background: #ffedd5;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  @media (max-width: 640px) {
    display: none;
  }
`;

const VaccinationDataRow = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 1fr 1fr 1fr;
  gap: 12px;
  padding: 10px 14px;
  font-size: 14px;
  border-top: 1px solid #fef3e2;
  align-items: center;
  &:first-of-type {
    border-top: none;
  }
  &:nth-child(even) {
    background: #fffdf9;
  }
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    gap: 4px;
    padding: 12px 14px;
  }
`;

const VaccineName = styled.span`
  font-weight: 600;
  color: #374151;
`;

const VaccineStatus = styled.span`
  font-weight: 600;
  color: ${(props) => (props.administered ? "#059669" : "#9ca3af")};
`;

const VaccineDate = styled.span`
  color: #4b5563;
`;

const VaccineBatch = styled.span`
  color: #6b7280;
  font-size: 13px;
`;

const VaccinationEmpty = styled.div`
  font-size: 14px;
  color: #9ca3af;
  font-style: italic;
  padding: 8px 0;
`;

const VaccinationRecordsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const VaccinationRecordBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const VaccinationRecordHeader = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #6b7280;
`;

const VaccinationMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 20px;
  font-size: 13px;
`;

const VaccinationMetaItem = styled.div`
  display: flex;
  gap: 6px;
  align-items: baseline;
`;

const MobileVaccineLabel = styled.span`
  display: none;
  font-size: 11px;
  font-weight: 600;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  @media (max-width: 640px) {
    display: inline;
    margin-right: 6px;
  }
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 4px;
`;

const Tag = styled.span`
  background: #f3f4f6;
  color: #4b5563;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
`;

const ObservationsBySurveyList = styled.div`
  display: flex;
  flex-direction: column;
  background: #fffbf7;
  border-radius: 8px;
  border: 1px solid #ffedd5;
  overflow: hidden;
`;

const ObservationHeaderRow = styled.div`
  display: grid;
  grid-template-columns: 140px 1fr;
  gap: 12px;
  padding: 10px 14px;
  background: #ffedd5;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const ObservationSurveyRow = styled.div`
  display: grid;
  grid-template-columns: 140px 1fr;
  gap: 12px;
  padding: 10px 14px;
  font-size: 14px;
  border-top: 1px solid #fef3e2;
  align-items: center;
  &:nth-child(even) {
    background: #fffdf9;
  }
`;

const ObservationSurveyLabel = styled.span`
  font-weight: 600;
  color: #374151;
`;

const ObservationIdList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  flex: 1;
`;

const ObservationIdChip = styled.span`
  background: #fff;
  color: #1f2937;
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  border: 1px solid #e5e7eb;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
`;

const ObservationsEmpty = styled.div`
  padding: 10px 14px;
  font-size: 13px;
  color: #9ca3af;
  background: #f9fafb;
  border-radius: 8px;
  border: 1px dashed #e5e7eb;
`;

const NoImagePlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  font-size: 14px;
  background: #f3f4f6;
`;

const LoadingText = styled.div`
  text-align: center;
  padding: 40px;
  color: #6b7280;
  font-size: 16px;
`;

const EmptyText = styled.div`
  text-align: center;
  padding: 40px;
  color: #9ca3af;
  font-size: 16px;
`;

const EditButton = styled.button`
  background: #c2410c;
  color: #fff;
  padding: 6px 14px;
  border-radius: 8px;
  border: none;
  font-weight: 600;
  cursor: pointer;
  font-size: 13px;
  flex-shrink: 0;
  transition: background 0.2s;
  &:hover {
    background: #9a3412;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const UndoButton = styled.button`
  background: #b45309;
  color: #fff;
  padding: 6px 14px;
  border-radius: 8px;
  border: none;
  font-weight: 600;
  cursor: pointer;
  font-size: 13px;
  flex-shrink: 0;
  transition: background 0.2s;
  &:hover {
    background: #92400e;
  }
  &:disabled {
    background: #d1d5db;
    cursor: not-allowed;
  }
`;

const SEX_CODE_MAP = { MALE: "M", FEMALE: "F", UNKNOWN: "U" };
const AGE_CODE_MAP = { PUP: "P", YOUNG: "Y", ADULT: "A", OLD: "O" };
const SIZE_CODE_MAP = { SMALL: "S", MEDIUM: "M", LARGE: "L" };
const PATTERN_CODE_MAP = {
  SOLID: "S",
  TRICOLOR: "TC",
  TIGER: "T",
  ZEBRA: "Z",
  MIXED: "BC",
};
const REPRO_CODE_MAP = {
  PREGNANT: "PREG",
  IN_HEAT: "HEAT",
  LACTATING: "LAC",
  NA: "NA",
};
const CONDITION_CODE_MAP = { POOR: "P", FAIR: "F", GOOD: "G" };

const csvEscape = (val) => {
  const s = val == null ? "" : String(val);
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
};

const getCollarFlag = (identification) => {
  if (typeof identification === "string") {
    return identification === "COLLAR" ? "Y" : "N";
  }
  if (identification && typeof identification === "object") {
    return identification.collarStatus === "COLLAR" ? "Y" : "N";
  }
  return "N";
};

const getNotchFlag = (dog) => {
  const f = dog?.fields || {};
  const val = dog?.notch_type_identication ?? f.notch_type_identication;
  return val === true ? "Y" : "N";
};

const getObservationsBySurvey = (dog) => dog?.observationsBySurvey || {};

const getSurveyObservationEntries = (observationsBySurvey) =>
  Object.entries(observationsBySurvey)
    .filter(([, ids]) => Array.isArray(ids) && ids.length > 0)
    .sort(([a], [b]) => {
      const numA = parseInt(a.match(/\d+/)?.[0] ?? "0", 10);
      const numB = parseInt(b.match(/\d+/)?.[0] ?? "0", 10);
      return numA - numB || a.localeCompare(b);
    });

const getTotalObservations = (observationsBySurvey) =>
  Object.values(observationsBySurvey).reduce(
    (sum, ids) => sum + (Array.isArray(ids) ? ids.length : 0),
    0,
  );

const MasterFiles = () => {
  const [routes, setRoutes] = useState([]);
  const [dogs, setDogs] = useState([]);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editModalDog, setEditModalDog] = useState(null);
  const [savingDog, setSavingDog] = useState(false);
  const [patchError, setPatchError] = useState(null);
  const [vaccinationsByDogId, setVaccinationsByDogId] = useState({});
  const [vaccinationsLoading, setVaccinationsLoading] = useState(false);
  const [undoingDogId, setUndoingDogId] = useState(null);
  const { token } = useAuth();

  useEffect(() => {
    fetchRoutes();
  }, []);

  const fetchRoutes = async () => {
    try {
      const res = await api.get("/api/dataanalysis/routes", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = res.data?.routes || res.data?.data || res.data || [];
      setRoutes(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching routes:", err);
    }
  };

  const fetchVaccinationsForDogs = async (dogList) => {
    if (!dogList.length) {
      setVaccinationsByDogId({});
      return;
    }

    setVaccinationsLoading(true);
    try {
      const results = await Promise.all(
        dogList.map(async (dog) => {
          try {
            const res = await api.get("/api/vaccinations", {
              params: { dogId: dog._id, limit: 100 },
              headers: { Authorization: `Bearer ${token}` },
            });
            let items = res.data?.data || [];
            const total = res.data?.pagination?.total ?? items.length;
            if (total > items.length) {
              const fullRes = await api.get("/api/vaccinations", {
                params: { dogId: dog._id, limit: total },
                headers: { Authorization: `Bearer ${token}` },
              });
              items = fullRes.data?.data || items;
            }
            return { dogId: dog._id, items };
          } catch (err) {
            console.error(
              `Error fetching vaccinations for dog ${dog._id}:`,
              err,
            );
            return { dogId: dog._id, items: [] };
          }
        }),
      );

      const map = {};
      results.forEach(({ dogId, items }) => {
        map[dogId] = items;
      });
      setVaccinationsByDogId(map);
    } finally {
      setVaccinationsLoading(false);
    }
  };

  const fetchDogs = async (routeId) => {
    setLoading(true);
    setDogs([]);
    setVaccinationsByDogId({});
    try {
      const res = await api.get("/api/dogs", {
        params: { routeId },
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = res.data?.data || [];
      const dogList = Array.isArray(data) ? data : [];
      setDogs(dogList);
      const route = routes.find((r) => r._id === routeId);
      setSelectedRoute(route);
      fetchVaccinationsForDogs(dogList);
    } catch (err) {
      console.error("Error fetching dogs:", err);
      setDogs([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRouteClick = (routeId) => {
    fetchDogs(routeId);
  };

  const handleBack = () => {
    setSelectedRoute(null);
    setDogs([]);
    setVaccinationsByDogId({});
  };

  const openEditModal = (dog) => {
    setPatchError(null);
    setEditModalDog(dog);
  };

  const closeEditModal = () => {
    if (savingDog) return;
    setEditModalDog(null);
    setPatchError(null);
  };

  const saveDogPatch = async (form) => {
    if (!editModalDog) return;
    setSavingDog(true);
    setPatchError(null);
    try {
      const payload = buildPatchPayload(form);
      const res = await api.patch(`/api/dogs/${editModalDog._id}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const updated = res.data?.data;
      if (updated) {
        setDogs((prev) =>
          prev.map((d) => (d._id === updated._id ? updated : d)),
        );
      }
      setEditModalDog(null);
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Failed to save dog";
      setPatchError(msg);
    } finally {
      setSavingDog(false);
    }
  };

  const handleUndoMerge = async (dog) => {
    const dogLabel = dog.fields?.dogUID || dog._id;
    const confirmed = window.confirm(
      `Undo the last merged observation for ${dogLabel}? This reverts the dog's details to the previous observation.`,
    );
    if (!confirmed) return;

    setUndoingDogId(dog._id);
    try {
      await api.post(
        `/api/dogs/${dog._id}/observations/undo`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      alert("Undo successful!");
      if (selectedRoute?._id) {
        await fetchDogs(selectedRoute._id);
      }
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Failed to undo merge";
      alert(msg);
    } finally {
      setUndoingDogId(null);
    }
  };

  const exportToCSV = () => {
    if (!dogs || dogs.length === 0) {
      alert("No dogs data to export");
      return;
    }

    // Find the maximum number of surveys across all dogs
    let maxSurveys = 0;
    dogs.forEach((dog) => {
      const dogFrequency = dog.dogFrequency || "";
      if (dogFrequency) {
        const surveyCount = dogFrequency.split("-").length;
        maxSurveys = Math.max(maxSurveys, surveyCount);
      }
    });

    // Create dynamic CSV header based on max surveys
    const headers = [
      "Dog ID",
      ...Array.from({ length: maxSurveys }, (_, i) => `Survey ${i + 1}`),
    ];

    // Create CSV rows
    const rows = dogs.map((dog) => {
      const dogUID = dog.fields?.dogUID || "";
      const dogFrequency = dog.dogFrequency || "";

      // Parse dogFrequency string (format: "1-1-0-0-0")
      const surveyValues = dogFrequency.split("-").filter((val) => val !== "");

      // Pad with "0" if needed to match maxSurveys
      const surveys = [];
      for (let i = 0; i < maxSurveys; i++) {
        surveys.push(surveyValues[i] || "0");
      }

      return [dogUID, ...surveys];
    });

    // Combine headers and rows
    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.join(",")),
    ].join("\n");

    // Create blob and download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `${selectedRoute?.name || "export"}_${new Date().toISOString().split("T")[0]}.csv`,
    );
    link.style.visibility = "hidden";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportMasterFileToCSV = () => {
    if (!dogs || dogs.length === 0) {
      alert("No dogs data to export");
      return;
    }

    const headers = [
      "DogID",
      "SEX",
      "AGE",
      "SIZE",
      "PATTERN",
      "Primary colour",
      "Secondary colour",
      "NTC",
      "Rep/STATUS",
      "COLLAR (Y/N)",
      "CONDITION",
      "LAT",
      "LONG",
      "Remarks",
    ];

    const rows = dogs.map((dog) => {
      const f = dog.fields || {};
      const demo = f.demographics || {};
      const app = f.appearance || {};
      const body = f.body || {};
      const repro = f.reproductiveStatus ?? dog.reproductiveStatus ?? "";

      return [
        f.dogUID || "",
        SEX_CODE_MAP[demo.gender] || "",
        AGE_CODE_MAP[demo.ageGroup] || "",
        SIZE_CODE_MAP[body.size] || "",
        PATTERN_CODE_MAP[app.coatPattern] || "",
        app.primaryColor || "",
        app.secondaryColor || "",
        getNotchFlag(dog),
        REPRO_CODE_MAP[repro] || "",
        getCollarFlag(f.identification),
        CONDITION_CODE_MAP[body.bodyCondition] || "",
        dog.homeCentroid?.latitude ?? "",
        dog.homeCentroid?.longitude ?? "",
        f.remarks || "",
      ];
    });

    const csvContent = [
      headers.map(csvEscape).join(","),
      ...rows.map((row) => row.map(csvEscape).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `${selectedRoute?.name || "export"}_master_file_${new Date().toISOString().split("T")[0]}.csv`,
    );
    link.style.visibility = "hidden";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const formatValue = (value) => {
    if (value === null || value === undefined) return "N/A";
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (Array.isArray(value)) return value.join(", ") || "None";
    return String(value);
  };

  const formatVaccineDate = (date) => {
    if (!date) return "—";
    return new Date(date).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const buildVaccinationRows = (record) => {
    if (!record) return [];

    const recordKey = record._id || "record";
    const rows = [
      {
        key: `${recordKey}-rabies`,
        label: "Rabies",
        ...(record.rabies || {}),
      },
      {
        key: `${recordKey}-distemper`,
        label: "Distemper",
        ...(record.distemper || {}),
      },
    ];

    (record.otherVaccines || []).forEach((vaccine, index) => {
      rows.push({
        key: vaccine._id || `${recordKey}-other-${index}`,
        label: vaccine.vaccineName || "Other vaccine",
        administered: vaccine.administered,
        date: vaccine.date,
        companyBatchNo: vaccine.companyBatchNo,
      });
    });

    return rows;
  };

  const renderVaccinationSection = (dogId) => {
    if (vaccinationsLoading) {
      return <VaccinationEmpty>Loading vaccination records…</VaccinationEmpty>;
    }

    const records = vaccinationsByDogId[dogId] || [];
    if (records.length === 0) {
      return <VaccinationEmpty>No vaccination records</VaccinationEmpty>;
    }

    return (
      <VaccinationRecordsList>
        {records.map((record, index) => {
          const rows = buildVaccinationRows(record);
          return (
            <VaccinationRecordBlock key={record._id || index}>
              <VaccinationRecordHeader>
                Record {records.length - index}
                {record.createdAt && (
                  <> · {formatVaccineDate(record.createdAt)}</>
                )}
              </VaccinationRecordHeader>
              <VaccinationMeta>
                <VaccinationMetaItem>
                  <InfoLabel>Blood collected:</InfoLabel>
                  <InfoValue>
                    {record.bloodCollected === "YES" ? "YES" : "NO"}
                  </InfoValue>
                </VaccinationMetaItem>
                <VaccinationMetaItem>
                  <InfoLabel>Serum extracted:</InfoLabel>
                  <InfoValue>
                    {record.serumExtracted === "YES" ? "YES" : "NO"}
                  </InfoValue>
                </VaccinationMetaItem>
              </VaccinationMeta>
              <VaccinationTable>
                <VaccinationHeaderRow>
                  <span>Vaccine</span>
                  <span>Status</span>
                  <span>Date</span>
                  <span>Batch No.</span>
                </VaccinationHeaderRow>
                {rows.map((row) => (
                  <VaccinationDataRow key={row.key}>
                    <VaccineName>{row.label}</VaccineName>
                    <VaccineStatus administered={row.administered}>
                      <MobileVaccineLabel>Status</MobileVaccineLabel>
                      {row.administered ? "Administered" : "Not administered"}
                    </VaccineStatus>
                    <VaccineDate>
                      <MobileVaccineLabel>Date</MobileVaccineLabel>
                      {formatVaccineDate(row.date)}
                    </VaccineDate>
                    <VaccineBatch>
                      <MobileVaccineLabel>Batch</MobileVaccineLabel>
                      {row.companyBatchNo || "—"}
                    </VaccineBatch>
                  </VaccinationDataRow>
                ))}
              </VaccinationTable>
            </VaccinationRecordBlock>
          );
        })}
      </VaccinationRecordsList>
    );
  };

  const ImageCarousel = ({ images, dogUID }) => {
    const [currentIndex, setCurrentIndex] = useState(0);

    if (!images || images.length === 0) {
      return (
        <ImageCarouselContainer>
          <CarouselWrapper>
            <NoImagePlaceholder>No images available</NoImagePlaceholder>
          </CarouselWrapper>
        </ImageCarouselContainer>
      );
    }

    const goToPrevious = () => {
      setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
    };

    const goToNext = () => {
      setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
    };

    const goToSlide = (index) => {
      setCurrentIndex(index);
    };

    return (
      <ImageCarouselContainer>
        <CarouselWrapper>
          {images.map((url, index) => (
            <CarouselImage
              key={index}
              src={url}
              alt={`Dog ${dogUID || ""} - Image ${index + 1}`}
              active={index === currentIndex}
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          ))}
          {images.length > 1 && (
            <>
              <CarouselButton direction="prev" onClick={goToPrevious}>
                ‹
              </CarouselButton>
              <CarouselButton direction="next" onClick={goToNext}>
                ›
              </CarouselButton>
            </>
          )}
        </CarouselWrapper>
        {images.length > 1 && (
          <CarouselDots>
            {images.map((_, index) => (
              <Dot
                key={index}
                active={index === currentIndex}
                onClick={() => goToSlide(index)}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </CarouselDots>
        )}
      </ImageCarouselContainer>
    );
  };

  if (selectedRoute) {
    return (
      <Page>
        <BackButton onClick={handleBack}>← Back to Routes</BackButton>
        <TitleContainer>
          <Title>Dogs in {selectedRoute.name}</Title>
          {!loading && dogs.length > 0 && (
            <HeaderActions>
              <ExportButton onClick={exportToCSV}>
                📥 Export Dog Frequency
              </ExportButton>
              <ExportButton onClick={exportMasterFileToCSV}>
                📥 Export Master File
              </ExportButton>
            </HeaderActions>
          )}
        </TitleContainer>
        {loading ? (
          <LoadingText>Loading dogs...</LoadingText>
        ) : dogs.length === 0 ? (
          <EmptyText>No dogs found for this route.</EmptyText>
        ) : (
          <DogsContainer>
            <DogsGrid>
              {dogs.map((dog) => {
                const observationsBySurvey = getObservationsBySurvey(dog);
                const surveyEntries =
                  getSurveyObservationEntries(observationsBySurvey);
                const totalObservations =
                  getTotalObservations(observationsBySurvey);

                return (
                  <DogCard key={dog._id}>
                    <ImageCarousel
                      images={dog.uploads || []}
                      dogUID={dog.fields?.dogUID}
                    />
                    <CardContent>
                      <DogCardHeader>
                        <DogUID>{dog.fields?.dogUID || "N/A"}</DogUID>
                        <HeaderActions>
                          <StatusBadge status={dog.status}>
                            {dog.status || "UNKNOWN"}
                          </StatusBadge>
                          <UndoButton
                            type="button"
                            onClick={() => handleUndoMerge(dog)}
                            disabled={
                              undoingDogId === dog._id || totalObservations <= 1
                            }
                            title={
                              totalObservations <= 1
                                ? "Cannot undo: the dog has only one observation"
                                : "Undo the last merged observation"
                            }
                          >
                            {undoingDogId === dog._id
                              ? "Undoing…"
                              : "Undo Merge"}
                          </UndoButton>
                          <EditButton
                            type="button"
                            onClick={() => openEditModal(dog)}
                          >
                            Edit
                          </EditButton>
                        </HeaderActions>
                      </DogCardHeader>

                      <DetailsGrid>
                        <Section>
                          <SectionTitle>Demographics</SectionTitle>
                          <InfoRow>
                            <InfoLabel>Age Group:</InfoLabel>
                            <InfoValue>
                              {formatValue(dog.fields?.demographics?.ageGroup)}
                            </InfoValue>
                          </InfoRow>
                          <InfoRow>
                            <InfoLabel>Gender:</InfoLabel>
                            <InfoValue>
                              {formatValue(dog.fields?.demographics?.gender)}
                            </InfoValue>
                          </InfoRow>
                          <InfoRow>
                            <InfoLabel>Breed:</InfoLabel>
                            <InfoValue>
                              {formatValue(dog.fields?.demographics?.breed)}
                              {dog.fields?.demographics?.breedOther &&
                                ` (${dog.fields.demographics.breedOther})`}
                            </InfoValue>
                          </InfoRow>
                        </Section>

                        <Section>
                          <SectionTitle>Appearance</SectionTitle>
                          <InfoRow>
                            <InfoLabel>Primary Color:</InfoLabel>
                            <InfoValue>
                              {formatValue(
                                dog.fields?.appearance?.primaryColor,
                              )}
                              {dog.fields?.appearance?.primaryColorOther &&
                                ` (${dog.fields.appearance.primaryColorOther})`}
                            </InfoValue>
                          </InfoRow>
                          <InfoRow>
                            <InfoLabel>Secondary Color:</InfoLabel>
                            <InfoValue>
                              {formatValue(
                                dog.fields?.appearance?.secondaryColor,
                              )}
                              {dog.fields?.appearance?.secondaryColorOther &&
                                ` (${dog.fields.appearance.secondaryColorOther})`}
                            </InfoValue>
                          </InfoRow>
                          <InfoRow>
                            <InfoLabel>Coat Pattern:</InfoLabel>
                            <InfoValue>
                              {formatValue(dog.fields?.appearance?.coatPattern)}
                            </InfoValue>
                          </InfoRow>
                        </Section>

                        <Section>
                          <SectionTitle>Body</SectionTitle>
                          <InfoRow>
                            <InfoLabel>Size:</InfoLabel>
                            <InfoValue>
                              {formatValue(dog.fields?.body?.size)}
                            </InfoValue>
                          </InfoRow>
                          <InfoRow>
                            <InfoLabel>Body Condition:</InfoLabel>
                            <InfoValue>
                              {formatValue(dog.fields?.body?.bodyCondition)}
                            </InfoValue>
                          </InfoRow>
                          {dog.fields?.remarks ? (
                            <InfoRow>
                              <InfoLabel>Remarks:</InfoLabel>
                              <InfoValue>
                                {formatValue(dog.fields.remarks)}
                              </InfoValue>
                            </InfoRow>
                          ) : null}
                        </Section>

                        <Section>
                          <SectionTitle>Identification</SectionTitle>
                          <InfoRow>
                            <InfoLabel>Identification:</InfoLabel>
                            <InfoValue>
                              {formatValue(
                                typeof dog.fields?.identification === "string"
                                  ? dog.fields.identification
                                  : dog.fields?.identification,
                              )}
                            </InfoValue>
                          </InfoRow>
                          {dog.fields?.qrCodeId && (
                            <InfoRow>
                              <InfoLabel>QR Code ID:</InfoLabel>
                              <InfoValue>{dog.fields.qrCodeId}</InfoValue>
                            </InfoRow>
                          )}
                        </Section>

                        <Section>
                          <SectionTitle>Other Details</SectionTitle>
                          <InfoRow>
                            <InfoLabel>Reproductive Status:</InfoLabel>
                            <InfoValue>
                              {formatValue(
                                dog.fields?.reproductiveStatus ??
                                  dog.reproductiveStatus,
                              )}
                            </InfoValue>
                          </InfoRow>
                          {dog.fields?.scars && dog.fields.scars.length > 0 && (
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "row",
                                gap: "8px",
                                alignItems: "center",
                              }}
                            >
                              <InfoLabel>Scars:</InfoLabel>
                              <TagsContainer>
                                {dog.fields.scars.map((scar, idx) => (
                                  <Tag key={idx}>{scar}</Tag>
                                ))}
                              </TagsContainer>
                            </div>
                          )}
                          {dog.fields?.spots && dog.fields.spots.length > 0 && (
                            <div
                              style={{
                                marginTop: "8px",
                                display: "flex",
                                flexDirection: "row",
                                gap: "8px",
                                alignItems: "center",
                              }}
                            >
                              <InfoLabel>Spots:</InfoLabel>
                              <TagsContainer>
                                {dog.fields.spots.map((spot, idx) => (
                                  <Tag key={idx}>{spot}</Tag>
                                ))}
                              </TagsContainer>
                            </div>
                          )}
                          {dog.fields?.injuries &&
                            dog.fields.injuries.length > 0 && (
                              <div
                                style={{
                                  marginTop: "8px",
                                  display: "flex",
                                  flexDirection: "row",
                                  gap: "8px",
                                  alignItems: "center",
                                }}
                              >
                                <InfoLabel>Injuries:</InfoLabel>
                                <TagsContainer>
                                  {dog.fields.injuries.map((injury, idx) => (
                                    <Tag key={idx}>{injury}</Tag>
                                  ))}
                                </TagsContainer>
                              </div>
                            )}
                        </Section>
                      </DetailsGrid>

                      <ObservationsBlock>
                        <SectionTitle>Observations</SectionTitle>
                        <ObservationsSummary>
                          <ObservationsSummaryItem>
                            <InfoLabel>Total Observations:</InfoLabel>
                            <InfoValue>{totalObservations}</InfoValue>
                          </ObservationsSummaryItem>
                          <ObservationsSummaryItem>
                            <InfoLabel>Dog frequency:</InfoLabel>
                            <InfoValue>{dog.dogFrequency || "—"}</InfoValue>
                          </ObservationsSummaryItem>
                          <ObservationsSummaryItem>
                            <InfoLabel>Home centroid:</InfoLabel>
                            <InfoValue>
                              {dog.homeCentroid?.latitude ?? "—"},{" "}
                              {dog.homeCentroid?.longitude ?? "—"}
                            </InfoValue>
                          </ObservationsSummaryItem>
                          {dog.observationIds?.length > 0 && (
                            <ObservationsSummaryItem>
                              <InfoLabel>Last seen:</InfoLabel>
                              <InfoValue>
                                {new Date(
                                  dog.observationIds[
                                    dog.observationIds.length - 1
                                  ]?.createdAt,
                                ).toLocaleString()}
                              </InfoValue>
                            </ObservationsSummaryItem>
                          )}
                        </ObservationsSummary>
                        {surveyEntries.length > 0 ? (
                          <ObservationsBySurveyList>
                            <ObservationHeaderRow>
                              <span>Survey</span>
                              <span>Observation IDs</span>
                            </ObservationHeaderRow>
                            {surveyEntries.map(
                              ([surveyName, observationIds]) => (
                                <ObservationSurveyRow key={surveyName}>
                                  <ObservationSurveyLabel>
                                    {surveyName}
                                  </ObservationSurveyLabel>
                                  <ObservationIdList>
                                    {observationIds.map(
                                      (observationId, idx) => (
                                        <ObservationIdChip
                                          key={`${surveyName}-${observationId}-${idx}`}
                                        >
                                          {observationId}
                                        </ObservationIdChip>
                                      ),
                                    )}
                                  </ObservationIdList>
                                </ObservationSurveyRow>
                              ),
                            )}
                          </ObservationsBySurveyList>
                        ) : (
                          <ObservationsEmpty>
                            No observations recorded
                          </ObservationsEmpty>
                        )}
                      </ObservationsBlock>

                      <VaccinationBlock>
                        <SectionTitle>Vaccinations</SectionTitle>
                        {renderVaccinationSection(dog._id)}
                      </VaccinationBlock>
                    </CardContent>
                  </DogCard>
                );
              })}
            </DogsGrid>
          </DogsContainer>
        )}
        {editModalDog && (
          <DogEditModal
            dog={editModalDog}
            title={`Edit dog — ${editModalDog.fields?.dogUID || editModalDog._id}`}
            saving={savingDog}
            error={patchError}
            onClose={closeEditModal}
            onSave={saveDogPatch}
          />
        )}
      </Page>
    );
  }

  return (
    <Page>
      <Title>Master Files - Select Route</Title>
      <Table>
        <thead>
          <tr>
            <Th>Name</Th>
            <Th>Total Surveys</Th>
            <Th>Start Location</Th>
            <Th>End Location</Th>
          </tr>
        </thead>
        <tbody>
          {routes.length === 0 ? (
            <tr>
              <Td colSpan="4" style={{ textAlign: "center", padding: "20px" }}>
                No routes found.
              </Td>
            </tr>
          ) : (
            routes.map((r) => (
              <RouteRow key={r._id} onClick={() => handleRouteClick(r._id)}>
                <Td>{r.name}</Td>
                <Td>{r.surveyIds?.length || 0}</Td>
                <Td>
                  {r.startLocation?.latitude?.toFixed(4)},{" "}
                  {r.startLocation?.longitude?.toFixed(4)}
                </Td>
                <Td>
                  {r.endLocation?.latitude?.toFixed(4)},{" "}
                  {r.endLocation?.longitude?.toFixed(4)}
                </Td>
              </RouteRow>
            ))
          )}
        </tbody>
      </Table>
    </Page>
  );
};

export default MasterFiles;
